import React, { useEffect, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Typography, Flex } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import DeleteFeaturedContentModal from '../components/DeleteFeaturedContentModal';
import FormFeaturedContent, { FeaturedContentFormData } from '../components/FormFeaturedContent';

const EditFeaturedContent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [formData, setFormData] = useState<FeaturedContentFormData | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { service: getDetailService } = useServices(ServiceNameEnum.featuredContent.detail);

  const getDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getDetailService.get({ params });
    if (data) {
      setIsPublished(data.isPublished ?? false);
      setFormData({
        type: data.type || 'event',
        eventId: data.eventId || null,
        newsId: data.newsId || null,
        displayOrder: data.displayOrder ?? 0,
        isPublished: data.isPublished ?? false,
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.featuredContent.index);
    }
  };

  useEffect(() => { getDetail(); }, []);

  const path: PathT[] = [
    { label: 'Featured Contents', value: RouteNameEnum.featuredContent.index },
    { label: 'Edit featured content' },
  ];

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Typography.Title level={4} style={{ margin: 0 }}>Edit Featured Content</Typography.Title>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={16}>
          <Typography.Text type="secondary">status</Typography.Text>
          <StatusBadge status={isPublished} />
        </Flex>
        <Flex gap={16}>
          {checkPermission([PermissionEnum.code.featuredContentDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteOutlined />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete
            </AlumlyButton>
          )}
        </Flex>
      </Flex>
      <DeleteFeaturedContentModal featuredContentId={Number(id)} isOpen={openDeleteModal} handleClose={() => setOpenDeleteModal(false)} />
      {formData && <FormFeaturedContent mode="edit" featuredContentId={id} defaultValues={formData} />}
    </div>
  );
};

export default EditFeaturedContent;
